import { ListRange } from '@angular/cdk/collections';
import { CdkVirtualScrollViewport, VirtualScrollStrategy } from '@angular/cdk/scrolling';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

/**
 * CDK virtual-scroll strategy for rows that don't all share one height —
 * photo-tile rows and year-separator rows in `VirtualPhotoGridComponent`.
 * The built-in `FixedSizeVirtualScrollStrategy` (the `itemSize` input) needs
 * one uniform row height, which forced every separator to reserve a full
 * tile-row's worth of vertical space (a large empty gap above each label).
 * This computes offsets from a `heights` array instead, one entry per row.
 */
export class RowHeightsVirtualScrollStrategy implements VirtualScrollStrategy {
  private readonly _scrolledIndexChange = new Subject<number>();
  readonly scrolledIndexChange: Observable<number> = this._scrolledIndexChange.pipe(distinctUntilChanged());

  private viewport: CdkVirtualScrollViewport | null = null;
  private heights: number[] = [];
  // offsets[i] = start offset of row i (px from top); offsets[heights.length] = total content size.
  private offsets: number[] = [0];
  private readonly bufferPx = 400;

  /** Recompute offsets for the current row heights and re-render. */
  setRowHeights(heights: number[]): void {
    this.heights = heights;
    const offsets = [0];
    let acc = 0;
    for (const h of heights) {
      acc += h;
      offsets.push(acc);
    }
    this.offsets = offsets;
    this.updateContent();
  }

  attach(viewport: CdkVirtualScrollViewport): void {
    this.viewport = viewport;
    this.updateContent();
  }

  detach(): void {
    this._scrolledIndexChange.complete();
    this.viewport = null;
  }

  onContentScrolled(): void {
    this.updateContent();
  }

  onDataLengthChanged(): void {
    this.updateContent();
  }

  onContentRendered(): void {
    // No-op — nothing to reconcile after render, offsets are computed upfront.
  }

  onRenderedOffsetChanged(): void {
    // No-op for the same reason.
  }

  scrollToIndex(index: number, behavior: ScrollBehavior): void {
    if (!this.viewport) return;
    const clamped = Math.max(0, Math.min(index, this.heights.length - 1));
    this.viewport.scrollToOffset(this.offsets[clamped] ?? 0, behavior);
  }

  private updateContent(): void {
    const viewport = this.viewport;
    if (!viewport) return;

    if (this.heights.length === 0) {
      viewport.setTotalContentSize(0);
      viewport.setRenderedRange({ start: 0, end: 0 });
      return;
    }

    viewport.setTotalContentSize(this.offsets[this.offsets.length - 1]);

    const scrollOffset = viewport.measureScrollOffset();
    const viewportSize = viewport.getViewportSize();

    const from = Math.max(0, scrollOffset - this.bufferPx);
    const to = scrollOffset + viewportSize + this.bufferPx;

    const start = this.rowAtOffset(from);
    const end = Math.min(this.heights.length, this.rowAtOffset(to) + 1);

    const range: ListRange = { start, end };
    viewport.setRenderedRange(range);
    viewport.setRenderedContentOffset(this.offsets[start]);
    this._scrolledIndexChange.next(this.rowAtOffset(scrollOffset));
  }

  /** Index of the last row whose start offset is <= target (binary search). */
  private rowAtOffset(target: number): number {
    if (target <= 0) return 0;
    let lo = 0;
    let hi = this.heights.length - 1;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      if (this.offsets[mid] <= target) lo = mid; else hi = mid - 1;
    }
    return lo;
  }
}
